import React, { useState, useEffect } from 'react';
import { Zap, Building2, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import PhoneAuthModal from '../components/PhoneAuthModal';
import B2BApplicationModal from '../components/B2BApplicationModal';
import { productAPI, userAPI } from '../utils/api';

function ProductListPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showB2BModal, setShowB2BModal] = useState(false);

  const handleB2BButtonClick = () => {
    if (user) {
      setShowB2BModal(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setShowLoginModal(false);
    loadProducts();
    // 로그인 후 바로 B2B 신청 모달 표시
    // setShowB2BModal(true);
  };

  useEffect(() => {
    loadProducts();
    loadUser();
  }, []);

  const loadUser = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const userData = await userAPI.getProfile();
        setUser(userData);
      } catch {
        // 토큰이 유효하지 않으면 무시
      }
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productAPI.getList();
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 일반 상품과 B2B 상품 분리 (B2B 유저인 경우에만)
  const isB2B = user?.user_type === 'B2B';
  const normalProducts = isB2B ? products.filter(p => p.product_type !== 'B2B') : products;
  const b2bProducts = isB2B ? products.filter(p => p.product_type === 'B2B') : [];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">전체 상품</h1>
          <p className="text-gray-500 mt-1">
            빠른 배송 가능한 상품은 <Zap className="w-4 h-4 inline text-teal-500" /> 표시가 있습니다
          </p>
        </div>

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
                <div className="aspect-square bg-gray-200" />
                <div className="p-4">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-1" />
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
                  <div className="h-6 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <p className="text-lg text-gray-600">등록된 상품이 없습니다.</p>
            <p className="text-sm text-gray-400 mt-1">곧 새로운 상품이 등록될 예정입니다.</p>
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <>
            {/* 전체 상품 목록 (B2B 유저는 일반 상품만, 일반 유저는 전체) */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
              {normalProducts.map((product) => (
                <ProductCard key={product.uuid} product={product} />
              ))}
            </div>

            {/* B2B 전용 상품 미리보기 (일반 유저에게 블러 처리해서 표시) */}
            {!isB2B && (
              <div className="mt-12">
                <div className="mb-6">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">B2B 전용 상품</h2>
                  </div>
                  <p className="text-gray-500 mt-1">
                    대량 구매 회원 전용 특별 가격으로 제공되는 상품입니다
                  </p>
                </div>

                {/* 블러된 스켈레톤 + CTA 오버레이 */}
                <div className="relative">
                  {/* 블러된 스켈레톤 카드들 */}
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 blur-sm select-none pointer-events-none opacity-60">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-white rounded-xl overflow-hidden">
                        <div className="aspect-square bg-gray-200" />
                        <div className="p-4">
                          <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                          <div className="h-4 bg-gray-100 rounded w-full mb-1" />
                          <div className="h-4 bg-gray-100 rounded w-2/3 mb-3" />
                          <div className="h-6 bg-gray-200 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA 오버레이 */}
                  <div className="absolute inset-0 flex items-center justify-center bg-white/30">
                    <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg max-w-md mx-4 text-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Building2 className="w-6 h-6 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">B2B 대량 구매 혜택</h3>
                      <p className="text-gray-500 text-sm mb-5">
                        최대 <span className="font-semibold text-purple-600">50% 할인</span>된 가격으로<br />
                        대량 구매가 가능합니다
                      </p>
                      <button
                        onClick={handleB2BButtonClick}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors"
                      >
                        B2B 회원 신청
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* B2B 상품 섹션 (B2B 유저에게만 표시) */}
            {isB2B && b2bProducts.length > 0 && (
              <div className="mt-12">
                <div className="mb-6">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">B2B 전용 상품</h2>
                  </div>
                  <p className="text-gray-500 mt-1">
                    B2B 회원 전용 특별 가격으로 제공되는 상품입니다
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                  {b2bProducts.map((product) => (
                    <ProductCard key={product.uuid} product={product} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />

      {/* 로그인 모달 */}
      <PhoneAuthModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
      />

      {/* B2B 신청 모달 */}
      <B2BApplicationModal
        isOpen={showB2BModal}
        onClose={() => setShowB2BModal(false)}
        user={user}
      />
    </div>
  );
}

export default ProductListPage;
